import React, { useEffect, useState } from 'react';
import { Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';

const fmtQty = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtCurrency = (n) => n != null ? Number(n).toLocaleString('vi-VN') + 'đ' : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('vi-VN') : '—';

const STATUS_COLOR = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const STATUS_LABEL = { PENDING: 'Chờ duyệt', APPROVED: 'Đã duyệt', REJECTED: 'Từ chối' };

const ImportRequestDetailModal = ({
  show,
  requestId,
  currentUserId,
  onHide,
  onApprove,
  onReject,
}) => {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (show && requestId) {
      setLoading(true);
      inventoryAPI.getImportRequest(requestId)
        .then((data) => setReq(data?.data ?? data))
        .catch(() => toast.error('Không thể tải phiếu nhập'))
        .finally(() => setLoading(false));
    } else if (!show) {
      setReq(null);
    }
  }, [show, requestId]);

  const handleApprove = async () => {
    if (!currentUserId) return;
    setActionLoading(true);
    try {
      await inventoryAPI.approveImportRequest(req.id, currentUserId);
      toast.success(`Đã duyệt phiếu ${req.request_number}`);
      onApprove?.();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Duyệt thất bại');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    onReject?.(req);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Chi tiết phiếu nhập kho
          {req && (
            <Badge bg={STATUS_COLOR[req.status] || 'secondary'} className="ms-2">
              {STATUS_LABEL[req.status] || req.status}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : !req ? (
          <p className="text-muted text-center">Không có dữ liệu</p>
        ) : (
          <>
            {/* Header info */}
            <div className="row g-3 mb-4">
              <div className="col-6">
                <small className="text-muted d-block">Mã phiếu</small>
                <strong>{req.request_number}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Chi nhánh</small>
                <span>{req.branch?.name}</span>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Người tạo</small>
                <span>{req.creator?.full_name}</span>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Thời gian tạo</small>
                <span>{fmtDateTime(req.created_at)}</span>
              </div>
              {req.supplier && (
                <div className="col-6">
                  <small className="text-muted d-block">Nhà cung cấp</small>
                  <span>{req.supplier.name}</span>
                </div>
              )}
              {req.invoice_number && (
                <div className="col-6">
                  <small className="text-muted d-block">Số hóa đơn</small>
                  <span>{req.invoice_number}</span>
                </div>
              )}
              {req.cash_voucher && (
                <div className="col-6">
                  <small className="text-muted d-block">Phiếu chi</small>
                  <span>{req.cash_voucher.voucher_number}</span>
                </div>
              )}
              {req.note && (
                <div className="col-12">
                  <small className="text-muted d-block">Ghi chú</small>
                  <span>{req.note}</span>
                </div>
              )}
              {req.approver && (
                <div className="col-6">
                  <small className="text-muted d-block">Người duyệt</small>
                  <span>{req.approver.full_name}</span>
                </div>
              )}
              {req.approved_at && (
                <div className="col-6">
                  <small className="text-muted d-block">Thời gian duyệt</small>
                  <span>{fmtDateTime(req.approved_at)}</span>
                </div>
              )}
              {req.rejected_reason && (
                <div className="col-12">
                  <small className="text-muted d-block">Lý do từ chối</small>
                  <span className="text-danger">{req.rejected_reason}</span>
                </div>
              )}
            </div>

            {/* Items table */}
            <Table bordered size="sm" className="mb-3">
              <thead className="table-light">
                <tr>
                  <th>NVL</th>
                  <th>Quy cách</th>
                  <th className="text-end">Số lượng</th>
                  <th className="text-end">SL cơ sở</th>
                  <th className="text-end">Đơn giá</th>
                  <th className="text-end">Thành tiền</th>
                </tr>
              </thead>
              <tbody>
                {(req.items || []).map((item) => (
                  <tr key={item.id}>
                    <td>{item.material?.name}</td>
                    <td>
                      {item.package
                        ? `${item.package.name}`
                        : `—`}
                    </td>
                    <td className="text-end">
                      {item.package_quantity ? `${fmtQty(item.package_quantity)} ${item.package?.name ?? ''}` : '—'}
                    </td>
                    <td className="text-end">
                      {fmtQty(item.quantity_in_base)} {item.material?.unit}
                    </td>
                    <td className="text-end">{fmtCurrency(item.unit_price)}</td>
                    <td className="text-end">{fmtCurrency(item.line_amount)}</td>
                  </tr>
                ))}
              </tbody>
              {req.total_amount && (
                <tfoot>
                  <tr>
                    <td colSpan={5} className="text-end fw-bold">Tổng cộng:</td>
                    <td className="text-end fw-bold">{fmtCurrency(req.total_amount)}</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={actionLoading}>
          Đóng
        </Button>
        {req?.status === 'PENDING' && (
          <>
            <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
              Từ chối
            </Button>
            <Button variant="success" onClick={handleApprove} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Duyệt phiếu'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportRequestDetailModal;
